import { _fetch } from "../../api";
import { API_V2_BASE_URL } from "../../constants/api";
import { getUserToken } from "../../helpers/authHelpers";
import { logRequest } from "../../utils/api";
import type {
  DataExportRegistryItem,
  DataExportHistoryItem,
  DataExportDownloadResponse,
  DataExportJobStatus,
  DataExportDownloadRequest,
  DataExportApiError,
} from "../../types/dataExport";

const BASE = `${API_V2_BASE_URL}/data-export`;

const buildError = async (response: Response): Promise<DataExportApiError> => {
  try {
    const payload = await response.json();
    return {
      status: response.status,
      error: payload?.error ?? payload?.errorMessage ?? "Request failed",
      errorMessage: payload?.errorMessage,
      details: payload,
    };
  } catch {
    return {
      status: response.status,
      error: `Request failed with status ${response.status}`,
    };
  }
};

/**
 * Parses the filename from a Content-Disposition header.
 * Returns null when the header is absent or unparseable — callers should
 * construct a fallback rather than using a generic name.
 *
 * Note: Content-Disposition is not a CORS-safelisted header. Browsers silently
 * return null for it on cross-origin responses unless the server sends
 * Access-Control-Expose-Headers: Content-Disposition. Always provide a fallback.
 */
const parseFileName = (contentDisposition: string | null): string | null => {
  if (!contentDisposition) return null;
  const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(
    contentDisposition
  );
  const encodedFileName = match?.[1] ?? match?.[2];
  if (!encodedFileName) return null;
  try {
    return decodeURIComponent(encodedFileName);
  } catch {
    return encodedFileName;
  }
};

const authorizedRequest = async (
  endpoint: string,
  init: RequestInit,
  retry = false
): Promise<Response> => {
  const token = await getUserToken(retry);
  const response = await fetch(endpoint, {
    ...init,
    headers: new Headers({
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(init.headers || {}),
    }),
  });

  if (response.status === 401 && !retry) {
    return authorizedRequest(endpoint, init, true);
  }

  return response;
};

/** GET /apiv2/data-export/registry — all available export types */
export const fetchDataExportRegistry = (): Promise<DataExportRegistryItem[]> =>
  _fetch({ method: "GET", endpoint: `${BASE}/registry` });

/** GET /apiv2/data-export/list — export history for the store */
export const fetchDataExportHistory = (): Promise<DataExportHistoryItem[]> =>
  _fetch({ method: "GET", endpoint: `${BASE}/list` });

/** POST /apiv2/data-export/download — trigger sync or async export */
export const triggerDataExport = (
  payload: DataExportDownloadRequest
): Promise<DataExportDownloadResponse> => {
  const endpoint = `${BASE}/download`;
  return authorizedRequest(endpoint, {
    method: "POST",
    body: JSON.stringify(payload),
    credentials: "same-origin",
  })
    .then(async (response) => {
      logRequest("POST", endpoint, response.status);
      if (!response.ok) {
        throw await buildError(response);
      }

      const contentType = response.headers.get("content-type") || "";
      if (contentType.toLowerCase().includes("text/csv")) {
        // Content-Disposition is not CORS-safelisted, so the header is null on
        // cross-origin responses unless Access-Control-Expose-Headers is set.
        // Fall back to constructing the filename from the exportId, matching
        // the server convention: `${exportId}_${YYYY-MM-DD}.csv`.
        const today = new Date().toISOString().slice(0, 10);
        const fallbackFileName = `${payload.exportId}_${today}.csv`;
        return {
          type: "sync",
          blob: await response.blob(),
          fileName:
            parseFileName(response.headers.get("content-disposition")) ??
            fallbackFileName,
        } as DataExportDownloadResponse;
      }

      const body = await response.json();
      return {
        type: "queued",
        jobId: body.jobId,
        status: "queued",
        exportId: body.exportId,
      } as DataExportDownloadResponse;
    })
    .catch((error) => {
      logRequest("POST", endpoint, error);
      throw error;
    });
};

/** GET /apiv2/data-export/queue/:jobId — poll async job status */
export const fetchDataExportJobStatus = (
  jobId: number
): Promise<DataExportJobStatus> => {
  const endpoint = `${BASE}/queue/${jobId}`;
  return authorizedRequest(endpoint, {
    method: "GET",
    credentials: "same-origin",
  })
    .then(async (response) => {
      logRequest("GET", endpoint, response.status);
      if (!response.ok) {
        throw await buildError(response);
      }
      return response.json();
    })
    .catch((error) => {
      logRequest("GET", endpoint, error);
      throw error;
    });
};

/** DELETE /apiv2/data-export/cancel/:jobId — cancel an in-progress async job */
export const cancelDataExportJob = (jobId: number): Promise<void> => {
  const endpoint = `${BASE}/cancel/${jobId}`;
  return authorizedRequest(endpoint, {
    method: "DELETE",
    credentials: "same-origin",
  })
    .then(async (response) => {
      logRequest("DELETE", endpoint, response.status);
      if (!response.ok) {
        throw await buildError(response);
      }
    })
    .catch((error) => {
      logRequest("DELETE", endpoint, error);
      throw error;
    });
};
